import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';

const CustomizedDepositCategories = () => {
  const [categoriesByType, setCategoriesByType] = useState({});
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('');
  const [depositTypes, setDepositTypes] = useState([]);
  const [newExpenseType, setNewExpenseType] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');

  // Default deposit types that cannot be edited or deleted
  const fixedDepositTypes = [
    'Grant Funding', 'Revenue', 'Investment', 'Loan',
    'Shareholder Injection', 'PMS Payment', 'Other',
    'Refund (Grant Item)', 'Refund (Non-grant Item)',
    'Internal Bank Transfer', 'N/A (Non-grant Item)'
  ];

  useEffect(() => {
    fetchCategoriesByType();
    fetchDepositTypes();
  }, []);

  const fetchCategoriesByType = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/by-deposit-type`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setCategoriesByType(data);
    } catch (error) {
      console.error('Error fetching categories by type:', error);
    }
  };

  const fetchDepositTypes = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/deposit-types`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      // Filter out fixed types so they can't be edited/deleted (case-insensitive)
      const fixedLower = fixedDepositTypes.map(f => f.toLowerCase());
      setDepositTypes(data.filter(t => !fixedLower.includes(t.toLowerCase())));
    } catch (error) {
      console.error('Error fetching deposit types:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    if (!newCategoryType) {
      alert('Please select a deposit type for this subcategory.');
      return;
    }
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories`, {
        category: newCategory,
        depositType: newCategoryType
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      setNewCategory('');
      fetchCategoriesByType();
    } catch (error) {
      console.error('Error adding customized category:', error);
      alert(error.message);
    }
  };

  const handleDeleteCategory = async (category, depositType) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/${encodeURIComponent(category)}${depositType ? `?depositType=${encodeURIComponent(depositType)}` : ''}`;
      const response = await api.delete(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      fetchCategoriesByType();
    } catch (error) {
      console.error('Error deleting customized category:', error);
      alert(error.message);
    }
  };

  const handleAddExpenseType = async () => {
    if (!newExpenseType.trim()) return;
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/deposit-types`, { type: newExpenseType });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setDepositTypes(data);
      setNewExpenseType('');
    } catch (error) {
      console.error('Error adding deposit type:', error);
      alert(error.message);
    }
  };

  const handleDeleteExpenseType = async (type) => {
    try {
      const response = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/deposit-types/${encodeURIComponent(type)}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setDepositTypes(data);
    } catch (error) {
      console.error('Error deleting deposit type:', error);
      alert(error.message);
    }
  };

  const handleEditExpenseType = async (oldType) => {
    if (!editTypeValue.trim() || editTypeValue.trim() === oldType) {
      setEditingType(null);
      return;
    }
    try {
      const response = await api.put(`${process.env.REACT_APP_BACKEND_API_URL}/deposit-categories/deposit-types/${encodeURIComponent(oldType)}`, { newType: editTypeValue });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setDepositTypes(data);
      setEditingType(null);
      fetchCategoriesByType(); // Refresh subcategories as deposit type name changed
    } catch (error) {
      console.error('Error editing deposit type:', error);
      alert(error.message);
    }
  };

  // All types that can have subcategories (fixed + user-added, deduplicated)
  const allTypesForSubcategories = [...new Set([...fixedDepositTypes, ...depositTypes])];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/grant-fund-financials?tab=unconfirmed" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Bank Statements
          </Link>
        </div>

        <div className="card p-6">
          <h1 className="text-2xl font-bold text-gray-900">Deposit Type / Subcategory</h1>
          <p className="text-gray-600 text-sm mt-1">Manage deposit types and their sub-categories for deposit transaction classification.</p>
        </div>

        {/* Deposit Types Card */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Deposit Types</h2>
          <p className="text-sm text-gray-600 mb-4">Grant-related deposit types used for categorizing transactions.</p>

          <div className="space-y-2 mb-4">
            {depositTypes.map((type) => (
              <div key={type} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
                {editingType === type ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={editTypeValue}
                      onChange={(e) => setEditTypeValue(e.target.value)}
                      className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleEditExpenseType(type); if (e.key === 'Escape') setEditingType(null); }}
                      autoFocus
                    />
                    <button onClick={() => handleEditExpenseType(type)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                    <button onClick={() => setEditingType(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm font-medium text-gray-900">{type}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditingType(type); setEditTypeValue(type); }}
                        className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteExpenseType(type)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {depositTypes.length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">No deposit types configured</p>
            )}
          </div>

          {/* Fixed Deposit Types (non-editable) */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">Fixed Types</p>
            <div className="space-y-2">
              {fixedDepositTypes.map((type) => (
                <div key={type} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                  <span className="text-sm font-medium text-gray-500">{type}</span>
                  <span className="text-xs text-gray-400">System</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newExpenseType}
              onChange={(e) => setNewExpenseType(e.target.value)}
              placeholder="Add new deposit type"
              className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddExpenseType(); }}
            />
            <button onClick={handleAddExpenseType} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              Add
            </button>
          </div>
        </div>

        {/* Subcategories by Deposit Type */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Subcategories by Deposit Type</h2>
          <p className="text-sm text-gray-600 mb-4">Each subcategory is assigned to a specific deposit type. When users select a deposit type in the transaction table, only its subcategories will appear.</p>

          <div className="space-y-4 mb-6">
            {allTypesForSubcategories.map((type) => {
              const typeSubcats = categoriesByType[type] || [];
              return (
                <div key={type} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">{type}</h3>
                    <span className="text-xs text-gray-500">{typeSubcats.length} subcategor{typeSubcats.length === 1 ? 'y' : 'ies'}</span>
                  </div>
                  <div className="p-3 space-y-1">
                    {typeSubcats.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-2">No subcategories yet</p>
                    ) : (
                      typeSubcats.map((subcat) => (
                        <div key={`${type}-${subcat}`} className="flex items-center justify-between px-3 py-2 rounded bg-white border border-gray-100">
                          <span className="text-sm text-gray-700">{subcat}</span>
                          <button
                            onClick={() => handleDeleteCategory(subcat, type)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                            title="Delete"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}

          </div>

          {/* Add new subcategory */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Add New Subcategory</h3>
            <div className="flex gap-2">
              <select
                value={newCategoryType}
                onChange={(e) => setNewCategoryType(e.target.value)}
                className="w-48 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select deposit type...</option>
                {allTypesForSubcategories.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Subcategory name"
                className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
              />
              <button onClick={handleAddCategory} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizedDepositCategories;
